import { useBreakpointValue } from '@chakra-ui/react';

/**
 * Shared "are we at Chakra's `lg` breakpoint (992px) or above" check.
 *
 * paire-app is a Vite SPA with no server-side rendering — there's no
 * first-request markup that a client hydration pass needs to stay
 * consistent with. But `useBreakpointValue`'s own `ssr` option defaults to
 * `true`, which assumes an SSR framework and deliberately resolves to the
 * smallest (`base`) breakpoint on the very first client render regardless
 * of the real viewport, only correcting itself once a layout effect fires
 * after mount. On a pure SPA that default produces a visibly wrong first
 * frame — e.g. `TabLayout.tsx`'s desktop nav rail (and its logo) missing
 * entirely at `lg`+ until that correction kicks in a tick later.
 *
 * `{ ssr: false }` makes Chakra read the real `window.matchMedia` result
 * synchronously on first render instead, so every consumer of this hook
 * gets the correct answer from its very first paint.
 *
 * Without this fix, the wrong first-render value doesn't just linger until
 * "a layout effect fires" — Chakra's `useMediaQuery` (verified against the
 * installed `@chakra-ui/react@2.10.9` source,
 * `media-query/use-media-query.mjs`) corrects itself in a plain `useEffect`,
 * i.e. after paint. That's a real, visible flash on the affected UI (e.g.
 * `TabLayout.tsx`'s desktop nav rail popping in a frame after first paint),
 * not merely a wasted extra render that resolves before anything hits the
 * screen.
 *
 * Every desktop/mobile breakpoint check anywhere in this app should go
 * through this hook rather than calling `useBreakpointValue({ base: false,
 * lg: true })` directly, so the `{ ssr: false }` fix (and the `?? false`
 * fallback for the brief window before Chakra resolves any value at all)
 * lives in exactly one place. An ESLint rule (`eslint.config.js`,
 * `no-restricted-syntax`) flags the direct-call pattern under `src/` to
 * keep this from regressing.
 */
export function useIsDesktop(): boolean {
  return useBreakpointValue({ base: false, lg: true }, { ssr: false }) ?? false;
}
